import { WixDesignSystemProvider } from '@wix/design-system';
import { StatusCompleteSmall } from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { ModalServiceContext, RicosContext } from './../../../ricos-context';
import type { PluginExports, TFunction } from './../../../ricos-types';
import { AiPlusButton } from './../../ai-plus-button/ai-plus-button';

import { AiContentModal } from '../../ai-content-modal/ai-content-modal';
import { AI_EXPORTS_CAPTION_TRIGGER_BUTTON } from '../../data-hooks';
import type { OpenDiscardModalFn, Prompt, ResultEntry } from '../../types';
import { AI_MODAL_ID } from '../../types';
import { getImageUrl } from '../../utils/get-image-url';

const CaptionTrigger: React.FC<
  React.ComponentProps<Required<PluginExports['aiIntegrations']['image']>['captionTrigger']> & {
    appDefId: string;
    openDiscardModal: OpenDiscardModalFn;
  }
> = ({ node, onCaptionChange, appDefId, openDiscardModal }) => {
  const modalsService = useContext(ModalServiceContext);
  const { localeContent, isMobile } = useContext(RicosContext);
  const [buttonRef, setButtonRef] = React.useState<HTMLButtonElement | null>(null);

  const openAiModal = () => {
    const initialPrompt = {
      type: 'prompt',
      title: 'AIPlugin_Prompt_GenerateCaption',
      promptSlug: 'generate-caption',
      promptParams: {
        imageUrl: getImageUrl(node.imageData),
        siteLanguage: localeContent,
      },
      additionalTags: 'image caption generation',
    } as const;

    modalsService.togglePluginPanel({
      id: AI_MODAL_ID,
      Component: AiContentModal,
      componentProps: {
        closeModal: () => modalsService.closeModal(AI_MODAL_ID),
        openDiscardModal: () => openDiscardModal(modalsService, buttonRef),
        config: {
          configurePrompts: () => ({ selectionPrompts: [], floatingToolbarButtonPrompts: [] }),
          appDefId,
        },
        feedUserEntryFormatter: (prompt: string | Prompt, t: TFunction) => {
          if (
            typeof prompt === 'object' &&
            'promptSlug' in prompt &&
            prompt.promptSlug === initialPrompt.promptSlug
          ) {
            return prompt.promptParams?.context ?? t(prompt.title);
          }

          return typeof prompt === 'string' ? prompt : t(prompt.title);
        },
        initialPromptOptions: {
          emptyStateTitle: 'AIPlugin_ImagePlugin_NoSelection_Title',
          emptyStateText: 'AIPlugin_ImagePlugin_NoSelection_Text',
          placeholder: 'AIPlugin_ImagePlugin_Input_Placeholder_Text',
          contextualResultActions: [
            {
              name: 'insert',
              type: 'action',
              title: 'AIPlugin_Action_Insert',
              icon: StatusCompleteSmall,
              onClick: async (entry: ResultEntry) => {
                onCaptionChange(entry.response.plainContent);
                modalsService.closeModal(AI_MODAL_ID);
              },
            },
          ],
          prompt: initialPrompt,
          autoTrigger: false,
          promptPayloadFormatter: (prompt: Prompt, textualPrompt: string) => {
            return {
              ...prompt,
              promptParams: {
                ...(prompt.promptParams ?? {}),
                context: textualPrompt,
              },
            };
          },
          isSubmitButtonVisible: () => true,
        },
      },
      positioning: {
        referenceElement: buttonRef,
        placement: 'bottom-end',
      },
      onClickOutside: () => {},
      entryType: 'image-caption',
      isMobile,
    });
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <AiPlusButton
        ref={setButtonRef}
        onClick={openAiModal}
        dataHook={AI_EXPORTS_CAPTION_TRIGGER_BUTTON}
      />
    </WixDesignSystemProvider>
  );
};

export const createCaptionTrigger = (appDefId: string, openDiscardModal: OpenDiscardModalFn) => {
  return (
    props: React.ComponentProps<
      Required<PluginExports['aiIntegrations']['image']>['captionTrigger']
    >
  ) => <CaptionTrigger {...props} appDefId={appDefId} openDiscardModal={openDiscardModal} />;
};
